import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell
} from "@/components/ui/table";
import {
  Users,
  Copy,
  DollarSign,
  TrendingUp,
  Link2,
  Share,
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  Star,
  Calculator,
  Gift,
  CheckCircle,
  Clock,
  Wallet
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exact match to categoryConfig.js CATEGORY_PRESETS
const CATEGORY_COLORS = {
  hair: '#e83e8c', makeup: '#9c57f5', nails: '#f15a70', lashes: '#774cda',
  events: '#f9c434', instructors: '#4a90d9', culinary: '#f28c28',
  wellness: '#75d6d6', travel: '#73b7ff', dispensary: '#60d680', retail: '#9950fa'
};

const CATEGORY_DEMOS = {
  hair: '/hair', makeup: '/mua', nails: '/nails', lashes: '/lash',
  events: '/events', instructors: '/instructor', culinary: '/culinary',
  wellness: '/wellness', travel: '/travel', dispensary: '/dispensaries', retail: '/retail'
};

const AdvocatePortalPage = ({ embedded = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [advocate, setAdvocate] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [simulatorCount, setSimulatorCount] = useState(25);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    fetchAdvocateData();
  }, []);

  const fetchAdvocateData = async () => {
    setLoading(true);
    try {
      const [advRes, earnRes, refRes] = await Promise.all([
        fetch(`${API}/advocate/me`, { headers: getAuthHeaders() }),
        fetch(`${API}/advocate/earnings`, { headers: getAuthHeaders() }),
        fetch(`${API}/advocate/referrals`, { headers: getAuthHeaders() })
      ]);
      
      if (advRes.ok) {
        const advData = await advRes.json();
        setAdvocate(advData);
      }
      if (earnRes.ok) {
        const earnData = await earnRes.json();
        setEarnings(earnData);
      }
      if (refRes.ok) {
        const refData = await refRes.json();
        setReferrals(refData.referrals || []);
      }
    } catch (err) {
      console.error("Error fetching advocate data:", err);
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getFullReferralLink = () => {
    if (!advocate?.referral_code) return "";
    return `${window.location.origin}/join?ref=${advocate.referral_code}`;
  };

  if (loading) {
    return (
      <div className={embedded ? "flex items-center justify-center py-12" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!advocate?.is_advocate) {
    return (
      <div className={embedded ? "flex items-center justify-center py-8" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Not an Advocate Yet</h2>
            <p className="text-gray-500 mb-4">You haven't been set up as an advocate. Contact an admin to get started.</p>
            {!embedded && <Button onClick={() => navigate("/me")} variant="outline">Back to Dashboard</Button>}
          </CardContent>
        </Card>
      </div>
    );
  }

  const commissionPct = advocate?.commission_percentage || 15;
  // Simulator is now calculated inline, no need for server-side simData

  return (
    <div className={embedded ? "" : "min-h-screen bg-gray-50"} data-testid="advocate-portal">
      {/* Header - hide when embedded */}
      {!embedded && (
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" className="text-white/80 hover:text-white" onClick={() => navigate("/me")}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <Star className="w-5 sm:w-6 h-5 sm:h-6" /> Advocate Portal
              </h1>
              <p className="text-emerald-100 mt-1 text-sm sm:text-base">
                {advocate?.first_name} {advocate?.last_name}  - {commissionPct}% Commission
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="text-center bg-white/10 rounded-xl px-3 sm:px-5 py-2 sm:py-3 flex-1 sm:flex-none">
                <p className="text-lg sm:text-2xl font-bold">{advocate?.total_referrals || 0}</p>
                <p className="text-[10px] sm:text-xs text-emerald-200">Referrals</p>
              </div>
              <div className="text-center bg-white/10 rounded-xl px-3 sm:px-5 py-2 sm:py-3 flex-1 sm:flex-none">
                <p className="text-lg sm:text-2xl font-bold">{advocate?.total_signups || 0}</p>
                <p className="text-[10px] sm:text-xs text-emerald-200">Signups</p>
              </div>
              <div className="text-center bg-white/10 rounded-xl px-3 sm:px-5 py-2 sm:py-3 flex-1 sm:flex-none">
                <p className="text-lg sm:text-2xl font-bold">${(advocate?.total_earnings || 0).toFixed(2)}</p>
                <p className="text-[10px] sm:text-xs text-emerald-200">Earned</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Tab Nav */}
      <div className={embedded ? "px-2" : "max-w-6xl mx-auto px-4 sm:px-6"}>
        <div className="flex gap-1 border-b bg-white rounded-t-xl mt-6 px-2 overflow-x-auto scrollbar-hide">
          {[
            { id: "overview", label: "Overview", icon: TrendingUp },
            { id: "referrals", label: "Referrals", icon: Users },
            { id: "simulator", label: "Simulator", icon: Calculator },
            { id: "payouts", label: "Payouts", icon: Wallet },
            { id: "promote", label: "Promote", icon: Share }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-2.5 sm:px-4 py-3 text-[11px] sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                activeTab === tab.id
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`advocate-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" /> {tab.id === 'overview' ? tab.label : <span className="hidden sm:inline">{tab.label}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={embedded ? "py-4 space-y-6" : "max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6"}>
        {/* OVERVIEW TAB */}
        {activeTab === "overview" && (
          <>
            {/* How it Works */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How Your Advocacy Works</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { step: 1, title: "Share Your Link", desc: "Share your unique referral link or code with anyone interested in the platform.", icon: Link2 },
                    { step: 2, title: "They Sign Up", desc: "When someone signs up using your link, they're tracked as your referral.", icon: Users },
                    { step: 3, title: "They Subscribe", desc: "When your referrals upgrade to a paid plan, you earn commission.", icon: DollarSign },
                    { step: 4, title: "Get Paid", desc: "Commissions are tracked and paid out via Stripe on a monthly basis.", icon: Wallet }
                  ].map(s => (
                    <div key={s.step} className="text-center p-4 rounded-xl border bg-gray-50">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3 font-bold">{s.step}</div>
                      <s.icon className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
                      <p className="font-medium text-sm">{s.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Referral Link Card */}
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2"><Link2 className="w-4 h-4 text-emerald-600" /> Your Referral Link</h3>
                    <p className="text-sm text-gray-600 mt-1">Share this link  - unlimited invites, no cap on earnings</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-1">{commissionPct}%</Badge>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Input
                    value={getFullReferralLink()}
                    readOnly
                    className="bg-white font-mono text-xs sm:text-sm"
                    data-testid="referral-link-input"
                  />
                  <Button onClick={() => copyToClipboard(getFullReferralLink())} className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Referral Code:</Label>
                    <code className="text-sm bg-white px-2 py-1 rounded border" data-testid="referral-code">{advocate?.referral_code}</code>
                    <Button variant="ghost" size="sm" className="h-6" onClick={() => copyToClipboard(advocate?.referral_code)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Total Referrals</p>
                      <p className="text-2xl font-bold">{advocate?.total_referrals || 0}</p>
                      <p className="text-xs text-gray-400">No limit</p>
                    </div>
                    <Users className="w-8 h-8 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Converted Signups</p>
                      <p className="text-2xl font-bold">{advocate?.total_signups || 0}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Total Earned</p>
                      <p className="text-2xl font-bold">${(advocate?.total_earnings || 0).toFixed(2)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Pending Payout</p>
                      <p className="text-2xl font-bold">${(advocate?.pending_payout || 0).toFixed(2)}</p>
                    </div>
                    <Clock className="w-8 h-8 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* REFERRALS TAB */}
        {activeTab === "referrals" && (
          <Card>
            <CardHeader>
              <CardTitle>My Referrals</CardTitle>
              <CardDescription>Everyone who signed up using your link or code</CardDescription>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No referrals yet</p>
                  <p className="text-sm">Share your link to start earning!</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((ref) => (
                      <TableRow key={ref.referral_id}>
                        <TableCell className="text-xs sm:text-sm">{ref.referred_email || " -"}</TableCell>
                        <TableCell>
                          {ref.category && (
                            <Badge variant="outline" className="capitalize text-xs" style={{ borderColor: CATEGORY_COLORS[ref.category] || '#6366F1', color: CATEGORY_COLORS[ref.category] || '#6366F1' }}>
                              {ref.category}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={ref.status === "converted" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}>
                            {ref.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{ref.subscription_tier || " -"}</TableCell>
                        <TableCell className="font-medium">${(ref.commission_earned || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-xs sm:text-sm text-gray-500">{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : " -"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SIMULATOR TAB */}
        {activeTab === "simulator" && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-emerald-600" /> Earnings Simulator</CardTitle>
                <CardDescription>
                  See how much you could earn at your {commissionPct}% commission rate. No limit on referrals.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <Label className="text-sm font-medium">Number of Referrals Who Subscribe</Label>
                  <div className="mt-2">
                    <Input
                      type="number"
                      min="1"
                      max="10000"
                      value={simulatorCount}
                      onChange={(e) => setSimulatorCount(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-40"
                      data-testid="simulator-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { name: "Solo Monthly", price: 11, period: "mo" },
                    { name: "Solo Annual", price: 110, period: "yr", monthlyEquiv: 9.17 },
                    { name: "Team Monthly", price: 44, period: "mo" },
                    { name: "Team Annual", price: 440, period: "yr", monthlyEquiv: 36.67 },
                  ].map((tier) => {
                    const perReferral = tier.price * commissionPct / 100;
                    const totalEarnings = perReferral * simulatorCount;
                    const annualTotal = tier.period === "mo" ? totalEarnings * 12 : totalEarnings;
                    return (
                      <Card key={tier.name} className="hover:border-emerald-300 transition-colors">
                        <CardContent className="p-5 text-center">
                          <Badge className="mb-3 bg-emerald-100 text-emerald-700">{tier.name}</Badge>
                          <p className="text-xs text-gray-500">${tier.price}/{tier.period} per subscriber</p>
                          <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                            <p className="text-xs text-gray-500">You earn per referral</p>
                            <p className="text-lg font-bold text-emerald-600">${perReferral.toFixed(2)}/{tier.period}</p>
                          </div>
                          <div className="mt-3 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">{simulatorCount} referrals</span>
                              <span className="font-bold">${totalEarnings.toFixed(2)}/{tier.period}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Annual total</span>
                              <span className="font-bold text-emerald-600">${annualTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                  <p className="text-sm text-gray-600">
                    <strong>How it works:</strong> You earn {commissionPct}% of the subscription fee for every user that signs up through your link.
                    For example, if {simulatorCount} people sign up for Solo ($11/mo), you'd earn{" "}
                    <strong className="text-emerald-600">
                      ${((11 * commissionPct / 100) * simulatorCount).toFixed(2)}/month
                    </strong>{" "}
                    (${((11 * commissionPct / 100) * simulatorCount * 12).toFixed(2)}/year). No cap on referrals.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* PAYOUTS TAB */}
        {activeTab === "payouts" && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <CardTitle>Payout History</CardTitle>
                  <CardDescription>Track all your commission payouts</CardDescription>
                </div>
                <div className="flex gap-4 text-left sm:text-right">
                  <div>
                    <p className="text-xs text-gray-500">Total Paid</p>
                    <p className="text-lg font-bold text-emerald-600">${(advocate?.total_paid || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending</p>
                    <p className="text-lg font-bold text-amber-600">${(advocate?.pending_payout || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(advocate?.payouts || []).length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No payouts yet</p>
                  <p className="text-sm">Payouts are processed monthly via Stripe once you reach the minimum threshold.</p>
                  <p className="text-xs text-gray-400 mt-2">Minimum payout: $50.00</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(advocate?.payouts || []).map((p) => (
                      <TableRow key={p.payout_id}>
                        <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="font-bold">${p.amount.toFixed(2)}</TableCell>
                        <TableCell className="capitalize">{p.method}</TableCell>
                        <TableCell><Badge className="bg-emerald-100 text-emerald-700">{p.status}</Badge></TableCell>
                        <TableCell className="text-sm text-gray-500">{p.notes || " -"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* PROMOTE TAB */}
        {activeTab === "promote" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Categories You Promote</CardTitle>
                <CardDescription>Share these demo pages with potential users. Each signup through your link earns you {commissionPct}% commission.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {(advocate?.categories?.length > 0 ? advocate.categories : Object.keys(CATEGORY_DEMOS)).map((cat) => (
                    <div key={cat} className="border rounded-xl p-3 sm:p-4 hover:shadow-md transition-shadow" data-testid={`promote-category-${cat}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold capitalize shrink-0" style={{ backgroundColor: CATEGORY_COLORS[cat] || '#6366F1' }}>
                          {cat.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium capitalize truncate">{cat}</p>
                          <p className="text-xs text-gray-500">Demo Page</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs sm:text-sm"
                          onClick={() => window.open(`${CATEGORY_DEMOS[cat] || `/${cat}`}?ref=${advocate?.referral_code || ''}`, '_blank')}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm"
                          onClick={() => copyToClipboard(`${window.location.origin}${CATEGORY_DEMOS[cat] || `/${cat}`}?ref=${advocate?.referral_code}`)}
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copy
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Share Assets */}
            <Card>
              <CardHeader>
                <CardTitle>Share Your Link</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800 mb-2">Your Unique Referral Link</p>
                  <div className="flex gap-2">
                    <Input value={getFullReferralLink()} readOnly className="bg-white font-mono text-sm" />
                    <Button onClick={() => copyToClipboard(getFullReferralLink())} className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
                      <Copy className="w-4 h-4 mr-1" /> Copy
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <p className="text-sm font-medium mb-2">Your Referral Code</p>
                  <div className="flex items-center gap-3">
                    <code className="text-lg font-mono bg-white px-4 py-2 rounded border">{advocate?.referral_code}</code>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(advocate?.referral_code)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Users can enter this code during signup to link their account to you.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvocatePortalPage;
